import React, { useState } from "react";
import * as XLSX from 'xlsx';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import api, { deleteContact, updateContact } from "../api";

export default function ContactTable({ contacts }) {
  const queryClient = useQueryClient();

  // Inline editing state
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  // Fetch groups for the dropdown
  const { data: groupsData } = useQuery({
    queryKey: ["groups"],
    queryFn: async () => {
      const { data } = await api.get("/contacts/show-groups");
      return data.data;
    }
  });

  const groups = groupsData || [];

  // Mutations
  const deleteMutation = useMutation({
    mutationFn: deleteContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (err) => alert("Failed to delete contact: " + err.message)
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => updateContact(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      setEditingId(null);
    },
    onError: (err) => alert("Failed to update contact: " + err.message)
  });

  const handleEdit = (contact) => {
    setEditingId(contact._id);
    setEditData({
      name: contact.name,
      phone: contact.phone,
      group: contact.group?._id || "",
      optedOut: contact.optedOut
    });
  };

  const handleSave = (id) => {
    updateMutation.mutate({ id, data: editData });
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this contact?")) {
      deleteMutation.mutate(id);
    }
  };

  const exportToExcel = () => {
    const exportData = contacts.map(contact => ({
      Name: contact.name || 'Unknown',
      Phone: contact.phone,
      Group: contact.group?.name || 'No Group',
      OptedOut: contact.optedOut ? 'Yes' : 'No'
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Contacts');

    const date = new Date().toISOString().split('T')[0];
    const filename = `contacts_${date}.xlsx`;
    XLSX.writeFile(workbook, filename);
  };

  return (
    <div className="yc-table-container">
      <div className="yc-table-actions" style={{ padding: '8px 20px', display: 'flex', justifyContent: 'flex-end', background: '#fff' }}>
        <button
          onClick={exportToExcel}
          className="yc-export-btn"
          style={{
            padding: '6px 12px',
            fontSize: '13px',
            backgroundColor: '#fff',
            color: '#2e7d32',
            border: '1px solid #2e7d32',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          <span>📥</span> Export to Excel
        </button>
      </div>

      <table className="yc-bc-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: '#f8f9fa' }}>
            <th style={{ textAlign: 'left', padding: '12px 20px', borderBottom: '2px solid #eee' }}>Name</th>
            <th style={{ textAlign: 'left', padding: '12px 20px', borderBottom: '2px solid #eee' }}>Phone</th>
            <th style={{ textAlign: 'left', padding: '12px 20px', borderBottom: '2px solid #eee' }}>Group Name</th>
            <th style={{ textAlign: 'left', padding: '12px 20px', borderBottom: '2px solid #eee' }}>Opted Out</th>
            <th style={{ textAlign: 'right', padding: '12px 20px', borderBottom: '2px solid #eee' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {contacts.length === 0 ? (
            <tr>
              <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#888' }}>
                No contacts found matching your criteria.
              </td>
            </tr>
          ) : (
            contacts.map((c) => (
              <tr key={c._id} className="yc-table-row" style={{ borderBottom: '1px solid #f0f0f0' }}>
                <td style={{ padding: '12px 20px' }}>
                  {editingId === c._id ? (
                    <input
                      className="yc-input"
                      style={{ width: '100%' }}
                      value={editData.name}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    />
                  ) : (
                    <span style={{ fontWeight: 500 }}>{c.name || 'Unknown'}</span>
                  )}
                </td>
                <td style={{ padding: '12px 20px', color: '#666' }}>
                  {editingId === c._id ? (
                    <input
                      className="yc-input"
                      style={{ width: '100%' }}
                      value={editData.phone}
                      onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    />
                  ) : (
                    c.phone
                  )}
                </td>
                <td style={{ padding: '12px 20px' }}>
                  {editingId === c._id ? (
                    <select
                      className="yc-input"
                      style={{ width: '100%' }}
                      value={editData.group}
                      onChange={(e) => setEditData({ ...editData, group: e.target.value })}
                    >
                      <option value="">No Group</option>
                      {groups.map(g => (
                        <option key={g._id} value={g._id}>{g.name}</option>
                      ))}
                    </select>
                  ) : (
                    <span className="yc-group-badge" style={{
                      padding: '4px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      backgroundColor: c.group ? '#f1f8e9' : '#f5f5f5',
                      color: c.group ? '#33691e' : '#666'
                    }}>
                      {c.group ? c.group.name : 'No Group'}
                    </span>
                  )}
                </td>
                <td style={{ padding: '12px 20px' }}>
                  {editingId === c._id ? (
                    <select
                      className="yc-input"
                      style={{ width: '100%' }}
                      value={editData.optedOut}
                      onChange={(e) => setEditData({ ...editData, optedOut: e.target.value === "true" })}
                    >
                      <option value="false">No</option>
                      <option value="true">Yes</option>
                    </select>
                  ) : (
                    <span style={{
                      padding: '4px 10px',
                      borderRadius: '20px',
                      fontSize: '11px',
                      fontWeight: 700,
                      backgroundColor: c.optedOut ? '#ffebee' : '#e8f5e9',
                      color: c.optedOut ? '#c62828' : '#2e7d32',
                      textTransform: 'uppercase'
                    }}>
                      {c.optedOut ? "Opted Out" : "Subscribed"}
                    </span>
                  )}
                </td>
                <td style={{ padding: '12px 20px', textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                    {editingId === c._id ? (
                      <>
                        <button
                          onClick={() => handleSave(c._id)}
                          style={{ background: '#4caf50', color: '#fff', padding: '6px 12px', borderRadius: '4px', border: 'none', cursor: 'pointer', fontSize: '13px' }}
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          style={{ background: '#eee', color: '#666', padding: '6px 12px', borderRadius: '4px', border: 'none', cursor: 'pointer', fontSize: '13px' }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleEdit(c)}
                          className="yc-edit-btn"
                          style={{ background: 'none', border: '1px solid #ddd', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px', color: '#1976d2' }}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(c._id)}
                          className="yc-delete-btn"
                          style={{ background: 'none', border: '1px solid #ddd', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px', color: '#d32f2f' }}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
